import { Link } from 'react-router-dom';

interface TopPick {
  label: string;
  productName: string;
  benefit: string;
  affiliateLink: string;
  articleSlug: string;
  image: string;
  accentColor: string;
  borderColor: string;
  badgeColor: string;
  badgeText: string;
}

const picks: TopPick[] = [
  {
    label: 'Best Gaming Monitor',
    productName: 'ASUS TUF Gaming VG27AQ',
    benefit: '1440p IPS at 165Hz, the sharpest and smoothest gaming image under $500.',
    affiliateLink: 'https://amzn.to/476Oifs',
    articleSlug: 'best-gaming-monitors-under-500',
    image: 'https://public.readdy.ai/ai/img_res/84bbf2df5196dc930ac42415c19df5ce.jpg',
    accentColor: 'from-purple-600/20 to-purple-900/5',
    borderColor: 'border-purple-500/20 hover:border-purple-400/50',
    badgeColor: 'bg-purple-600/20 text-purple-300 border-purple-500/30',
    badgeText: 'Gaming Tech',
  },
  {
    label: 'Best Gaming Keyboard',
    productName: 'SteelSeries Apex Pro',
    benefit: 'Adjustable actuation per key, the most customizable competitive keyboard ever made.',
    affiliateLink: 'https://amzn.to/4rGUeTM',
    articleSlug: 'best-mechanical-keyboards-for-gaming',
    image: 'https://readdy.ai/api/search-image?query=SteelSeries%20Apex%20Pro%20mechanical%20keyboard%20with%20vivid%20OLED%20display%20and%20RGB%20backlight%20floating%20on%20dark%20desk%20setup%20moody%20atmosphere%20purple%20and%20cyan%20accent%20lighting%20ultra%20realistic%20product%20photography&width=600&height=420&seq=top-pick-keyboard-v1&orientation=landscape',
    accentColor: 'from-cyan-600/20 to-cyan-900/5',
    borderColor: 'border-cyan-500/20 hover:border-cyan-400/50',
    badgeColor: 'bg-cyan-600/20 text-cyan-300 border-cyan-500/30',
    badgeText: 'Gaming Tech',
  },
  {
    label: 'Best Streaming Mic',
    productName: 'Shure MV7',
    benefit: 'USB + XLR in one mic, starts plug-and-play and scales to a full studio setup.',
    affiliateLink: 'https://amzn.to/40HAyEi',
    articleSlug: 'best-microphones-for-streaming',
    image: 'https://readdy.ai/api/search-image?query=Shure%20MV7%20hybrid%20USB%20XLR%20streaming%20microphone%20on%20boom%20arm%20in%20dark%20content%20creator%20studio%20setup%20with%20soft%20amber%20and%20purple%20tones%20elegant%20professional%20product%20photography%20clean%20minimal%20background&width=600&height=420&seq=top-pick-shure-mv7-v1&orientation=landscape',
    accentColor: 'from-amber-600/20 to-amber-900/5',
    borderColor: 'border-amber-500/20 hover:border-amber-400/50',
    badgeColor: 'bg-amber-600/20 text-amber-300 border-amber-500/30',
    badgeText: 'Streaming',
  },
  {
    label: 'Best Work Monitor',
    productName: 'Dell UltraSharp U2723QE',
    benefit: '4K IPS with factory-calibrated colors, the gold standard for home office productivity.',
    affiliateLink: 'https://amzn.to/4dsonTe',
    articleSlug: 'best-monitors-for-work-and-productivity',
    image: 'https://readdy.ai/api/search-image?query=Dell%20UltraSharp%20U2723QE%204K%20monitor%20displaying%20crisp%20productivity%20content%20on%20minimalist%20office%20desk%20with%20natural%20light%20plants%20and%20clean%20surroundings%20professional%20workspace%20product%20photography%20elegant%20background&width=600&height=420&seq=top-pick-dell-u2723-v1&orientation=landscape',
    accentColor: 'from-emerald-600/20 to-emerald-900/5',
    borderColor: 'border-emerald-500/20 hover:border-emerald-400/50',
    badgeColor: 'bg-emerald-600/20 text-emerald-300 border-emerald-500/30',
    badgeText: 'Office Productivity',
  },
];

export default function TopPicksRightNow() {
  return (
    <section className="py-14 relative overflow-hidden">
      {/* Ambient glows */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-purple-500/6 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-amber-400/30 rounded-full text-xs uppercase tracking-widest text-amber-400/90 bg-amber-400/5 mb-4">
            <i className="ri-flashlight-fill text-amber-400"></i>
            Updated March 2026
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-3">
            Top Picks Right Now
            <div className="h-1 w-32 bg-gradient-to-r from-amber-500 via-purple-500 to-cyan-500 mx-auto mt-4 rounded-full shadow-lg shadow-amber-500/30"></div>
          </h2>
          <p className="text-gray-400 text-base max-w-xl mx-auto">
            The best-reviewed product in each category. Skip the research and go straight to the best.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {picks.map((pick) => (
            <div
              key={pick.label}
              className={`group relative flex flex-col rounded-2xl border ${pick.borderColor} bg-gradient-to-b ${pick.accentColor} backdrop-blur-sm overflow-hidden transition-all duration-400 hover:shadow-2xl hover:shadow-black/40 hover:-translate-y-1`}
            >
              {/* Invisible full-card link overlay */}
              <a
                href={pick.affiliateLink}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="absolute inset-0 z-10 cursor-pointer"
                aria-label={`View ${pick.productName} on Amazon`}
              />

              {/* Top glow bar */}
              <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

              {/* Image */}
              <div className="relative w-full h-48 overflow-hidden flex-shrink-0">
                <img
                  src={pick.image}
                  alt={pick.productName}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

                {/* Badge */}
                <div className="absolute top-3 left-3">
                  <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full border backdrop-blur-sm ${pick.badgeColor}`}>
                    {pick.badgeText}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="flex flex-col flex-1 p-5 gap-3">
                {/* Label */}
                <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
                  {pick.label}
                </p>

                {/* Product name */}
                <h3 className="text-base font-bold text-white leading-snug group-hover:text-amber-300 transition-colors duration-300">
                  {pick.productName}
                </h3>

                {/* Benefit */}
                <p className="text-sm text-gray-400 leading-relaxed flex-1">
                  {pick.benefit}
                </p>

                {/* Buttons */}
                <div className="flex flex-col gap-2 mt-auto relative z-20">
                  <a
                    href={pick.affiliateLink}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="block w-full text-center px-4 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold rounded-lg text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Check Price on Amazon →
                  </a>
                  <Link
                    to={`/article/${pick.articleSlug}`}
                    className="block w-full text-center px-4 py-2 border border-white/15 text-gray-400 hover:text-white hover:border-white/30 rounded-lg text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View Full Review
                  </Link>
                </div>
              </div>

              {/* Bottom glow bar */}
              <div className="h-px w-full bg-gradient-to-r from-transparent via-white/8 to-transparent" />
            </div>
          ))}
        </div>

        {/* Trust line */}
        <p className="text-center text-xs text-gray-600 mt-6">
          Verified Amazon availability &middot; No inflated pricing &middot; Updated for 2026
        </p>
      </div>
    </section>
  );
}
